import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { exerciseRunsApi, exercisesApi, type Exercise, type ExerciseRun } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'

/**
 * Задания и работы урока — один запрос на урок.
 *
 * Раньше их грузила панель, смонтированная под каждой темой: у урока с тремя
 * темами это был тройной запрос за одними и теми же данными. Теперь данные
 * живут в одном месте и раздаются и списку темы, и блоку работ вне программы.
 */
export type LessonExercisesData = {
  /** Все задания программы урока, по всем его темам. */
  exercises: Exercise[]
  /** Работы урока: ученику сервер отдаёт только его. */
  runs: ExerciseRun[]
  /** Состав программы урока: по нему отличаем работу, выданную мимо программы. */
  programExerciseIds: Set<number>
  loading: boolean
  error: string | null
  setError: (value: string | null) => void
  /** Перечитать данные; `background` — без скелетона. */
  reload: (background?: boolean) => Promise<void>
}

export function useLessonExercises(
  lessonId: number,
  adminReview: boolean,
  refreshToken: number,
): LessonExercisesData {
  const { t } = useTranslation()

  const [exercises, setExercises] = useState<Exercise[]>([])
  const [runs, setRuns] = useState<ExerciseRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(
    async (background = false) => {
      // Обновление по событию урока идёт фоном: иначе на каждую сдачу любого
      // ученика весь блок мигал бы скелетоном.
      if (!background) setLoading(true)
      setError(null)
      try {
        const [loaded, lessonRuns] = await Promise.all([
          exercisesApi.listByLesson(lessonId, adminReview),
          // Ученику приходят только его попытки — список тот же, фильтрует сервер
          exerciseRunsApi.listByLesson(lessonId, adminReview),
        ])
        setExercises(loaded)
        setRuns(lessonRuns)
      } catch (err) {
        setError(getErrorMessage(err) || t('common.error'))
      } finally {
        setLoading(false)
      }
    },
    [adminReview, lessonId, t],
  )

  const initialisedRef = useRef(false)

  useEffect(() => {
    void reload(initialisedRef.current)
    initialisedRef.current = true
  }, [reload, refreshToken])

  const programExerciseIds = useMemo(
    () => new Set(exercises.map((exercise) => exercise.id)),
    [exercises],
  )

  return { exercises, runs, programExerciseIds, loading, error, setError, reload }
}
